export const INFINITE_GALLERY_VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

export const INFINITE_GALLERY_FRAGMENT_SHADER = /* glsl */ `
  varying vec2 vUv;

  uniform sampler2D uImageAtlas;
  uniform sampler2D uTextAtlas;
  uniform sampler2D uLayoutTexture;
  uniform sampler2D uActiveVideoTexture;

  uniform vec4 uBorderColor;
  uniform vec4 uHoverColor;
  uniform vec4 uBackgroundColor;
  uniform vec2 uOffset;
  uniform vec2 uResolution;
  uniform vec2 uHoveredCell;
  uniform vec2 uPrevHoveredCell;

  uniform float uZoom;
  uniform float uAtlasSize;
  uniform float uColumnWidth;
  uniform float uNumColumns;
  uniform float uCellGap;
  uniform float uTexRows;
  uniform float uTextureCount;
  uniform float uHoverIntensity;
  uniform float uPrevHoverIntensity;
  uniform float uDistortion;
  uniform float uVignetteIntensity;
  uniform float uVignetteRadius;
  uniform float uVignetteSoftness;
  uniform float uRadialBlurIntensity;
  uniform float uRadialBlurSamples;
  uniform float uActiveVideoIndex;
  uniform float uIntroProgress;

  float sdCircle(vec2 p, float r) {
    return length(p) - r;
  }

  float sdPlayTriangle(vec2 p, float r) {
      const float k = 1.7320508;
      p = vec2(-p.y, p.x);
      p.x = abs(p.x) - r;
      p.y = p.y + r / k;
      if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
      p.x -= clamp(p.x, -2.0 * r, 0.0);
      return -length(p) * sign(p.y);
  }

  // Helper function to sample color at a given screen coordinate
  vec3 sampleColor(vec2 screenUV) {
    float radius = length(screenUV);
    float distortion = 1.0 - uDistortion * radius * radius;
    vec2 distortedUV = screenUV * distortion;

    vec2 aspectRatio = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 worldCoord = distortedUV * aspectRatio;

    worldCoord *= uZoom;
    worldCoord += uOffset;

    // Total width of all columns
    float totalWidth = uNumColumns * uColumnWidth;
    
    // Determine which column we're in (with infinite horizontal tiling)
    float wrappedX = mod(worldCoord.x, totalWidth);
    if (wrappedX < 0.0) wrappedX += totalWidth;
    
    int col = int(floor(wrappedX / uColumnWidth));
    float colPatternX = floor(worldCoord.x / totalWidth);
    
    // Position within the column (0 to columnWidth)
    float localX = mod(wrappedX, uColumnWidth);
    
    // Get column metadata from layout texture (row 0)
    vec4 colMeta = texture2D(uLayoutTexture, vec2((float(col) + 0.5) / 64.0, 0.5 / uTexRows));
    float colTotalHeight = colMeta.r;
    float colYOffset = colMeta.g;
    float cellCount = colMeta.b;
    
    // Apply column Y offset and wrap vertically based on column's total height
    float colY = worldCoord.y - colYOffset;
    float colPatternY = floor(colY / colTotalHeight);
    float localY = mod(colY, colTotalHeight);
    if (localY < 0.0) localY += colTotalHeight;
    
    // Base background
    vec3 color = uBackgroundColor.rgb;
    
    // Find which cell in this column contains localY
    float foundCell = -1.0;
    float cellY = 0.0;
    float cellHeight = 0.0;
    float cellImageIndex = 0.0;
    float cellIsVideo = 0.0;

    // Sample cells from column's row in texture (rows 1, 2, 3 for columns 0, 1, 2)
    float textureRow = (float(col) + 1.5) / uTexRows;
    int lo = 0;
    int hi = int(cellCount) - 1;

    for (int step = 0; step < 7; step++) {
      if (lo > hi) break;
      int mid = (lo + hi) / 2;
      vec4 cellData = texture2D(uLayoutTexture, vec2((float(mid) + 0.5) / 64.0, textureRow));
      float cY = cellData.r;
      float cH = cellData.g;

      if (localY >= cY && localY < cY + cH) {
          foundCell = float(mid);
          cellY = cY;
          cellHeight = cH;
          cellImageIndex = cellData.b;
          cellIsVideo = cellData.a;
          break;
      } else if (localY < cY) {
          hi = mid - 1;
      } else {
          lo = mid + 1;
      }
    }
    
    if (foundCell >= 0.0) {
      // Calculate UV within cell
      float cellUVx = localX / uColumnWidth;
      float cellUVy = (localY - cellY) / cellHeight;
      
      // Apply gap
      float gapSize = uCellGap;
      vec2 gapCellUV = (vec2(cellUVx, cellUVy) - gapSize / 2.0) / (1.0 - gapSize);
      bool inCellArea = gapCellUV.x >= 0.0 && gapCellUV.x <= 1.0 && gapCellUV.y >= 0.0 && gapCellUV.y <= 1.0;
      
      if (inCellArea) {
        float cellCenterX = (colPatternX * uNumColumns + float(col) + 0.5) * uColumnWidth;
        float cellCenterY = colPatternY * colTotalHeight + colYOffset + cellY + cellHeight * 0.5;
        vec2 cellWorldCenter = vec2(cellCenterX, cellCenterY);

        float staggerSpread  = 3.5;
        float staggerDelay   = 0.5;
        float maxDist        = 0.5;
        float distFromOrigin = length(cellWorldCenter);
        float radialDelay    = clamp(distFromOrigin / staggerSpread, 0.0, staggerDelay);
        float cellIntro      = clamp((uIntroProgress - radialDelay) / (1.0 - radialDelay),
                                     0.0,
                                     1.0);

        cellIntro = cellIntro * cellIntro * (3.0 - 2.0 * cellIntro);

        if (cellIntro < 0.001) return uBackgroundColor.rgb;

        float cellIdX = colPatternX * uNumColumns * 100.0 + float(col) * 100.0 + foundCell;
        vec2 cellId = vec2(cellIdX, colPatternY);
        
        bool isCurrentHovered = cellId.x == uHoveredCell.x && cellId.y == uHoveredCell.y;
        bool isPrevHovered = cellId.x == uPrevHoveredCell.x && cellId.y == uPrevHoveredCell.y;
        
        float cellHoverIntensity = 0.0;
        if (isCurrentHovered) {
          cellHoverIntensity = uHoverIntensity;
        } else if (isPrevHovered) {
          cellHoverIntensity = uPrevHoverIntensity;
        }

        // Image fills cell completely when not hovered
        vec2 imageUV = gapCellUV;
        float imageAlpha = 1.0;

        float introScale = 1.0 + (1.0 - cellIntro) * 0.25;
        vec2 scaledImageUV = (imageUV - 0.5) * introScale + 0.5;
        imageUV = mix(scaledImageUV, imageUV, cellIntro);
        
        // When hovered, apply margins and smooth edges
        if (cellHoverIntensity > 0.0) {
          float smallMargin = 0.01 * cellHoverIntensity;
          // float bottomMargin = 0.05 * cellHoverIntensity;
          
          float imageLeft = smallMargin;
          float imageRight = 1.0 - smallMargin;
          float imageTop = smallMargin; // use bottom margin for polaroid effect
          float imageBottom = 1.0 - smallMargin;
          
          float imageWidth = imageRight - imageLeft;
          float imageHeight = imageBottom - imageTop;
          
          imageUV = vec2(
            (gapCellUV.x - imageLeft) / imageWidth,
            (gapCellUV.y - imageTop) / imageHeight
          );

          float edgeSmooth = 0.005;
          vec2 imageMask = smoothstep(0.0, edgeSmooth, imageUV) * smoothstep(0.0, edgeSmooth, 1.0 - imageUV);
          imageAlpha = imageMask.x * imageMask.y;
        }

        bool inImageArea = imageUV.x >= 0.0 && imageUV.x <= 1.0 && imageUV.y >= 0.0 && imageUV.y <= 1.0;

        float texIndex = cellImageIndex;
        
        // Use hover color as background only when hovering
        color = cellHoverIntensity > 0.0 ? uHoverColor.rgb : uBackgroundColor.rgb;

        if (inImageArea) {
            float atlasCol = mod(texIndex, uAtlasSize);
            float atlasRow = floor(texIndex / uAtlasSize);
            float flippedRow = uAtlasSize - 1.0 - atlasRow;
            vec2 atlasPos = vec2(atlasCol, flippedRow);
            vec2 atlasUV = (atlasPos + clamp(imageUV, 0.001, 0.999)) / uAtlasSize;

            vec3 imageColor;
            if (uActiveVideoIndex >= 0.0 && texIndex == uActiveVideoIndex) {
                imageColor = texture2D(uActiveVideoTexture, clamp(imageUV, 0.001, 0.999)).rgb;
            } else {
                imageColor = texture2D(uImageAtlas, atlasUV).rgb;
            }

            color = mix(color, imageColor, imageAlpha);
        }
        
        float textDisplaySize = uColumnWidth * 0.9;
        float worldX = gapCellUV.x * uColumnWidth;
        float worldY = gapCellUV.y * cellHeight;
        vec2 textUV = vec2(worldX / textDisplaySize, worldY / textDisplaySize);

        float gradientHeight = .45;
        float gradientOpacity = .85;
        float gradientStart = 1.0 - gradientHeight;
        float gradientAlpha = smoothstep(gradientStart, 1.0, 1. - gapCellUV.y) * gradientOpacity * cellHoverIntensity * imageAlpha;
        color = mix(color, vec3(0.0), gradientAlpha);
        
        if (textUV.x >= 0.0 && textUV.x <= 1.0 && textUV.y >= 0.0 && textUV.y <= 1.0) {
          float atlasCol = mod(texIndex, uAtlasSize);
          float atlasRow = floor(texIndex / uAtlasSize);
          float flippedRow = uAtlasSize - 1.0 - atlasRow;
          vec2 atlasPos = vec2(atlasCol, flippedRow - ((1. - cellHoverIntensity) * .1));
          
          vec2 textAtlasUV = (atlasPos + clamp(textUV, 0.001, 0.999)) / uAtlasSize;
          vec4 textColor = texture2D(uTextAtlas, textAtlasUV);
          color = mix(color, textColor.rgb, textColor.a * cellHoverIntensity);
        }
        
        if (cellIsVideo > 0.5) {
          float iconFade = 1.0 - (cellHoverIntensity * 1.5);

          if (iconFade > 0.01) {
            float cellAspect = cellHeight / uColumnWidth;
            vec2 iconCenter  = vec2(0.075 / uColumnWidth, 0.07 / cellHeight - (cellHoverIntensity * .1));
            float iconRadius = 0.032;

            vec2 d = gapCellUV - iconCenter;
            d.y *= cellAspect;

            float circle = smoothstep(0.002, -0.002, sdCircle(d, iconRadius));
            float triangle = smoothstep(0.004, -0.004, sdPlayTriangle(d * 1.05, iconRadius * 0.38));

            float circleAlpha = circle * iconFade * 0.7;
            vec3 iconColor = mix(color, vec3(0.0), circleAlpha);
            iconColor = mix(iconColor, vec3(0.85), triangle * iconFade);
            color = iconColor;
          }
        }

        color = mix(uBackgroundColor.rgb, color, cellIntro);
      }
    }
    
    return color;
  }

  void main() {
    vec2 screenUV = (vUv - 0.5) * 2.0;
    vec3 color = sampleColor(screenUV);

    // RADIAL BLUR - NEW
    // Apply radial blur on the edges by sampling outward from center
    float distanceFromCenter = length(screenUV);
    float blurFactor = smoothstep(uVignetteRadius - 0.3, uVignetteRadius + 0.2, distanceFromCenter);
    blurFactor *= uRadialBlurIntensity;

    if (uRadialBlurIntensity > 0.01 && blurFactor > 0.01) {
      vec3 blurColor = color;
      float samples = max(2.0, uRadialBlurSamples);
      
      for (float i = 1.0; i < samples; i++) {
        float sampleDist = mix(0.0, blurFactor * 0.15, i / samples);
        vec2 direction = normalize(screenUV + vec2(0.001));
        vec2 sampleUV = screenUV + direction * sampleDist;
        blurColor += sampleColor(sampleUV);
      }
      
      color = blurColor / (samples);
    }

    // VIGNETTE EFFECT
    float vignetteRadius = length(screenUV);
    float vignette = smoothstep(uVignetteRadius, uVignetteRadius - uVignetteSoftness, vignetteRadius);
    vignette = mix(1.0, vignette, uVignetteIntensity);

    float fade = 1.0 - smoothstep(1.2, 1.8, length(screenUV));
    color *= fade;
    color *= vignette;

    gl_FragColor = vec4(color, 1.0);
  }
`
